/**
 * Per-property manual cash-flow entries, backed by `manual_transactions`.
 *
 * Powers the "Add expense" action on the property detail view. Reuses the same
 * table the Accounting page reads, so a logged expense shows up in both places
 * — one source of truth, no forked storage.
 */
import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useWorkspace } from "@/hooks/use-workspace";
import { toUserMessage } from "@/lib/supabase-errors";

export interface PropertyExpense {
  id: string;
  transaction_date: string;
  amount: number;
  type: "income" | "expense";
  category: string;
  description: string | null;
  vendor: string | null;
}

export interface NewExpense {
  transaction_date: string;
  amount: number;
  type: "income" | "expense";
  category: string;
  description?: string | null;
  vendor?: string | null;
}

export function usePropertyExpenses(pipelinePropertyId?: string | null) {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const queryClient = useQueryClient();
  const workspaceId = currentWorkspace?.id;

  const key = useMemo(
    () => ["property-expenses", pipelinePropertyId] as const,
    [pipelinePropertyId],
  );

  const query = useQuery({
    queryKey: key,
    enabled: Boolean(workspaceId && pipelinePropertyId),
    queryFn: async (): Promise<PropertyExpense[]> => {
      if (!workspaceId || !pipelinePropertyId) return [];
      const { data, error } = await supabase
        .from("manual_transactions")
        .select("id, transaction_date, amount, type, category, description, vendor")
        .eq("workspace_id", workspaceId)
        .eq("pipeline_property_id", pipelinePropertyId)
        .order("transaction_date", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as PropertyExpense[];
    },
  });

  const createExpense = useMutation({
    mutationFn: async (input: NewExpense) => {
      if (!workspaceId || !user || !pipelinePropertyId) {
        throw new Error("Missing workspace / property");
      }
      const { error } = await supabase.from("manual_transactions").insert({
        workspace_id: workspaceId,
        created_by_user_id: user.id,
        pipeline_property_id: pipelinePropertyId,
        transaction_date: input.transaction_date,
        amount: input.amount,
        type: input.type,
        category: input.category,
        description: input.description ?? null,
        vendor: input.vendor ?? null,
        is_tax_deductible: true,
      } as never);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Expense added");
      void queryClient.invalidateQueries({ queryKey: ["property-expenses", pipelinePropertyId] });
      void queryClient.invalidateQueries({ queryKey: ["manual-transactions"] });
    },
    onError: (e: unknown) => toast.error(toUserMessage(e, "Failed to add expense")),
  });

  const deleteExpense = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("manual_transactions").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Expense removed");
      void queryClient.invalidateQueries({ queryKey: ["property-expenses", pipelinePropertyId] });
      void queryClient.invalidateQueries({ queryKey: ["manual-transactions"] });
    },
    onError: (e: unknown) => toast.error(toUserMessage(e, "Failed to remove expense")),
  });

  return {
    expenses: query.data ?? [],
    isLoading: query.isLoading,
    createExpense,
    deleteExpense,
  };
}
